import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import {
  FormControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { TUI_DEFAULT_MATCHER, TuiStringHandler } from '@taiga-ui/cdk';
import { TuiValueContentContext } from '@taiga-ui/core';
import { OrganisationService, UserService } from '@src/core/services';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { Nullable } from '@src/types/utils';
import { BehaviorSubject } from 'rxjs';
import { NgxRolesService } from 'ngx-permissions';
import { RoleCode } from '@src/app/modules/auth';
import { OrganisationView } from '@src/api';
import { TranslateService } from '@ngx-translate/core';
import { inputPhoneFormValidator } from '@src/app/modules/phone';

import { selectItemMatcher } from '../../../utils';
import { SelectItem } from '../../../types';
import { LoyaltyProgramProductAddress, LoyaltyProgramProductInput, LoyaltyProgramProductPerson } from '../../../models';
import { AbstractStepComponent } from '../abstract-step/abstract-step.component';

import { ContactsStepFormData, ContactsStepFormDataControls } from './types';
import { mapObjectValidator } from './validators/mapObjectValidator';

@Component({
  selector: 'lp-product-contacts-step',
  templateUrl: './contacts-step.component.html',
  styleUrls: ['./contacts-step.component.scss'],
  // TODO: поменял стратегию на `default` т.к. основной диалоге при вызове `cdr.markForCheck()` не работает
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ContactsStepComponent extends AbstractStepComponent {
  private readonly organisationService = inject(OrganisationService);
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly ngxRolesService = inject(NgxRolesService);
  private readonly userService = inject(UserService);
  private readonly translateService = inject(TranslateService);

  readonly companyContent: TuiStringHandler<TuiValueContentContext<SelectItem>> = ({ $implicit: result }) =>
    result ? result.name : this.translateService.instant('components.loyaltyContactsStep.labels.notSelected');
  readonly companyMatcher = selectItemMatcher;
  readonly companyStringify: TuiStringHandler<SelectItem> = item => item.name ?? '';

  private readonly search$ = new BehaviorSubject<string>('');
  private readonly organisationList$ = new BehaviorSubject<OrganisationView[]>([]);
  private readonly isAdminUO;
  private readonly userOrganisationIds: string[];

  readonly filteredCompanies$ = this.search$.pipe(
    startWith(''),
    switchMap(search =>
      this.organisationList$.pipe(
        map(items => {
          return (
            items
              ?.filter(({ shortName }) => TUI_DEFAULT_MATCHER(shortName, search))
              .map(item => ({
                id: item.id ?? '',
                name: item.shortName ?? '',
              })) ?? []
          );
        }),
      ),
    ),
  );

  form = new UntypedFormGroup(<ContactsStepFormDataControls>{
    company: new UntypedFormControl(null, Validators.required),
    companyInfo: new UntypedFormControl(null, [Validators.maxLength(2000)]),
    contacts: this.formBuilder.array([], Validators.required),
    mentor: new UntypedFormControl(null),
    mapObjects: new FormControl<LoyaltyProgramProductAddress[]>([], {
      nonNullable: true,
      validators: mapObjectValidator,
    }),
  });

  constructor() {
    super();

    this.isAdminUO = RoleCode.AdminUO in this.ngxRolesService.getRoles();
    this.userOrganisationIds =
      this.userService.authUser?.organisationJobTitles?.map(item => item.organisationId!) ?? [];

    this.organisationService.organisationList$
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(result =>
        this.organisationList$.next(
          (this.isAdminUO ? result : result?.filter(item => this.userOrganisationIds.includes(item.id!))) ?? [],
        ),
      );
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.emitForm(this.form);
    this.organisationService.getOrganisationList();
  }

  emitChanges() {
    const { company, companyInfo, contacts, mentor, mapObjects } = this.form.value as ContactsStepFormData;

    return {
      contacts: {
        company: {
          id: company?.id ?? '',
          name: company?.name ?? '',
          info: companyInfo ?? '',
        },
        mentor: mentor
          ? {
              id: mentor.id ?? '',
              name: [mentor.lastName, mentor.firstName, mentor.middleName].filter(item => !!item).join(' '),
            }
          : undefined,
        persons: contacts ?? [],
      },
      mapObjects,
    };
  }

  get contacts(): UntypedFormArray {
    return this.form.get('contacts') as UntypedFormArray;
  }

  addContact(contact?: LoyaltyProgramProductPerson) {
    this.contacts.push(
      this.formBuilder.group({
        id: new UntypedFormControl(contact?.id),
        contactName: new UntypedFormControl(contact?.contactName, [Validators.maxLength(200)]),
        contactPhone: new UntypedFormControl(contact?.contactPhone, [inputPhoneFormValidator()]),
        contactEmail: new UntypedFormControl(contact?.contactEmail, [Validators.email, Validators.maxLength(200)]),
        address: new UntypedFormControl(contact?.address, [Validators.maxLength(1000)]),
        website: new UntypedFormControl(contact?.website, [Validators.required, Validators.maxLength(1000)]),
      }),
    );
  }

  removeContact(index: number): void {
    this.contacts.removeAt(index);
  }

  onSearchCompany(query: Nullable<string>) {
    this.search$.next(query ?? '');
  }

  protected patchProduct({ contacts, mapObjects }: LoyaltyProgramProductInput) {
    const { persons, mentor } = contacts ?? {};

    const company = contacts?.company.id
      ? {
          id: contacts?.company.id,
          logoId: contacts?.company.logoId,
          name: contacts?.company.name,
          info: contacts?.company.info,
        }
      : undefined;

    this.form.patchValue(<ContactsStepFormData>{
      company,
      companyInfo: company?.info,
      mentor: mentor ? { id: mentor.id, fullName: mentor.name } : undefined,
      mapObjects,
    });

    if (persons && persons.length > 0) {
      persons.forEach(contact => this.addContact(contact));
    } else {
      this.addContact();
    }
  }
}
